export const waitFor = (timeInMS: number, shouldReject = false): Promise<void> => {
    return new Promise((resolve, reject) => {
        window.setTimeout(() => {
            if (shouldReject) {
                reject();
            } else {
                resolve();
            }
        }, timeInMS);
    });
};

export const withTimeout = (promise: Promise<void>, timeInMS: number): Promise<void> => {
    return Promise.race([promise, waitFor(timeInMS, true)]);
};

/**
 * Utility method to poll to @param pollingUntilResolved
 * at certain interval (@param pollIntervalTimeInMs)
 * for certain duration (@param pollingDurationInMs)
 * Ignores any error on catch as this is polling
 */
export const poll = (
    pollingDurationInMs: number,
    pollIntervalTimeInMs: number,
    pollingUntilResolved: () => Promise<void>,
): [Promise<void>, VoidFunction] => {
    let intervalRef: number | undefined;
    let isCanceled = false;

    const pollFn = async (resolvePollingPromise: VoidFunction): Promise<void> => {
        if (isCanceled) {
            return;
        }

        try {
            await pollingUntilResolved();
            if (isCanceled) {
                return;
            }

            return resolvePollingPromise();
        } catch (e) {
            // Ignore
        }

        intervalRef = window.setTimeout(() => pollFn(resolvePollingPromise), pollIntervalTimeInMs);
    };

    const pollingPromise = new Promise<void>(resolve => {
        pollFn(resolve);
    });

    const promise = withTimeout(pollingPromise, pollingDurationInMs).catch(_ => {
        // ignore
    });

    const cancel = (): void => {
        isCanceled = true;
        window.clearTimeout(intervalRef);
    };

    return [promise, cancel];
};
